import React, { useState, useEffect } from 'react';
import { 
  FaTrashAlt, FaUsers, FaSearch, FaTimes, 
  FaBoxOpen, FaExclamationTriangle, FaArrowRight, FaStore 
} from 'react-icons/fa';

// --- Types (Logic unchanged) ---
interface Buyer { id: number; username: string; email: string; mobile: string; isVerified: boolean; }
interface Seller { id: number; companyName: string; username: string; email: string; mobile: string; isVerified: boolean; }
interface Product { id: number; productName: string; price: number; description: string; status: string; category: { id: number; name: string; }; images: { imageUrl: string }[]; }

const UsersManagement: React.FC = () => {
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'buyers'|'sellers' | null>('buyers');
  const [popupSeller, setPopupSeller] = useState<Seller | null>(null);
  const [sellerProducts, setSellerProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resBuyers = await fetch('http://localhost:2027/api/admin/buyers');
      if(resBuyers.ok) setBuyers(await resBuyers.json());
      const resSellers = await fetch('http://localhost:2027/api/admin/sellers-activity');
      if(resSellers.ok) setSellers(await resSellers.json());
    } catch (error) { console.error("Error fetching users:", error); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSellerClick = async (seller: Seller) => {
    setPopupSeller(seller);
    setLoadingProducts(true);
    try {
      const res = await fetch(`http://localhost:2027/api/admin/seller-products/${seller.id}`);
      if(res.ok) setSellerProducts(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoadingProducts(false); }
  };

  const deleteProduct = async (productId: number) => {
    if(!window.confirm("Remove this product permanently?")) return;
    try {
      const res = await fetch(`http://localhost:2027/api/admin/delete-product/${productId}`, { method: 'DELETE' });
      if(res.ok) setSellerProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) { console.error(error); }
  };

  const getGroupedProducts = () => {
    const grouped: Record<string, Product[]> = {};
    sellerProducts.forEach(prod => {
      const catName = prod.category?.name || "Uncategorized";
      if(!grouped[catName]) grouped[catName] = [];
      grouped[catName].push(prod);
    });
    return grouped;
  };

  return (
    <div className="space-y-10 pb-10">
      {/* Header Section */}
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tight">Users Management</h2>
          <p className="text-gray-400 font-medium mt-1">Manage your ecosystem of buyers and sellers</p>
        </div>
        <div className="flex gap-3 bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100">
           <button 
             onClick={() => setActiveTab('buyers')}
             className={`px-6 py-2.5 rounded-xl text-xs font-black tracking-widest transition-all ${activeTab === 'buyers' ? 'bg-[#006d5b] text-white shadow-lg shadow-[#006d5b]/20' : 'text-gray-400 hover:text-gray-600'}`}
           >
             BUYERS
           </button>
           <button 
             onClick={() => setActiveTab('sellers')}
             className={`px-6 py-2.5 rounded-xl text-xs font-black tracking-widest transition-all ${activeTab === 'sellers' ? 'bg-[#006d5b] text-white shadow-lg shadow-[#006d5b]/20' : 'text-gray-400 hover:text-gray-600'}`}
           >
             SELLERS
           </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-[35px] shadow-sm border border-gray-100 flex items-center justify-between group">
           <div>
              <p className="text-[10px] font-black text-gray-400 tracking-[0.2em] uppercase mb-2">Authenticated Buyers</p>
              <h3 className="text-4xl font-black text-gray-900">{buyers.length}</h3>
           </div>
           <div className="w-16 h-16 bg-[#006d5b]/5 rounded-[22px] flex items-center justify-center text-[#006d5b]">
              <FaUsers size={28} />
           </div>
        </div>
        <div className="bg-white p-8 rounded-[35px] shadow-sm border border-gray-100 flex items-center justify-between group">
           <div>
              <p className="text-[10px] font-black text-gray-400 tracking-[0.2em] uppercase mb-2">Registered Sellers</p>
              <h3 className="text-4xl font-black text-gray-900">{sellers.length}</h3>
           </div>
           <div className="w-16 h-16 bg-[#006d5b]/5 rounded-[22px] flex items-center justify-center text-[#006d5b]">
              <FaStore size={26} />
           </div>
        </div>
      </div>

      {/* Main List Area */}
      <div className="bg-white rounded-[40px] shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-10 py-8 border-b border-gray-50 flex justify-between items-center">
           <h3 className="text-xl font-black text-gray-900 capitalize">{activeTab} Directory</h3>
           <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
              <input type="text" placeholder="Search users..." className="bg-gray-50 border-none rounded-xl py-2 pl-10 pr-4 text-xs focus:ring-2 focus:ring-[#006d5b]/10 w-64" />
           </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              <tr>
                <th className="px-10 py-5">Identification</th>
                <th className="px-6 py-5">{activeTab === 'sellers' ? 'Company Details' : 'Full Name'}</th>
                <th className="px-6 py-5">Contact Information</th>
                <th className="px-6 py-5">Account Status</th>
                <th className="px-10 py-5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm font-bold divide-y divide-gray-50">
              {(activeTab === 'buyers' ? buyers : sellers).map((user) => (
                <tr key={user.id} className="group hover:bg-gray-50/50 transition-colors">
                  <td className="px-10 py-6 text-gray-400 font-medium text-xs">#{user.id.toString().padStart(4, '0')}</td>
                  <td className="px-6 py-6">
                    <div className="text-gray-900 font-black">
                      {(user as Seller).companyName || user.username}
                    </div>
                    {activeTab === 'sellers' && <div className="text-[10px] text-gray-400 uppercase mt-0.5">Prop: {user.username}</div>}
                  </td>
                  <td className="px-6 py-6">
                    <div className="text-gray-700">{user.email}</div>
                    <div className="text-gray-400 text-xs mt-1 font-medium">{user.mobile}</div>
                  </td>
                  <td className="px-6 py-6">
                    {user.isVerified ? (
                      <span className="px-3 py-1 bg-emerald-50 text-[#006d5b] text-[10px] font-black rounded-lg uppercase tracking-tighter">Verified</span>
                    ) : (
                      <span className="px-3 py-1 bg-gray-100 text-gray-400 text-[10px] font-black rounded-lg uppercase tracking-tighter">Pending</span>
                    )}
                  </td>
                  <td className="px-10 py-6 text-right">
                    {activeTab === 'sellers' ? (
                      <button 
                        onClick={() => handleSellerClick(user as Seller)}
                        className="p-3 bg-gray-900 text-white rounded-xl hover:bg-[#006d5b] transition-all shadow-md flex items-center gap-2 text-[10px] font-black ml-auto"
                      >
                        MANAGE PRODUCTS <FaArrowRight size={10} className="text-white/50" />
                      </button>
                    ) : (
                      <button className="text-gray-400 hover:text-red-500 transition-colors p-2">
                        <FaTrashAlt size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* POPUP: SELLER PRODUCTS (Refined Modal) */}
      {popupSeller && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-md flex items-center justify-center z-[60] p-6" onClick={() => setPopupSeller(null)}>
          <div className="bg-white rounded-[40px] w-full max-w-5xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-white/20" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-10 border-b border-gray-50 flex justify-between items-center">
               <div>
                  <p className="text-[10px] font-black text-[#006d5b] uppercase tracking-widest mb-1">Portfolio Inspector</p>
                  <h2 className="text-3xl font-black text-gray-900">{popupSeller.companyName}</h2>
               </div>
               <button onClick={()=>setPopupSeller(null)} className="w-12 h-12 bg-gray-50 hover:bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 transition-all">
                 <FaTimes size={20}/>
               </button>
            </div>

            {/* Modal Body */}
            <div className="p-10 overflow-y-auto bg-[#fafafa]">
              {loadingProducts ? (
                 <div className="flex flex-col items-center py-20 animate-pulse">
                    <div className="w-12 h-12 border-4 border-[#006d5b] border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-4 text-xs font-black text-gray-400 uppercase tracking-widest">Scanning Database...</p>
                 </div>
              ) : sellerProducts.length === 0 ? (
                 <div className="text-center py-20 flex flex-col items-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center text-gray-300 mb-4">
                      <FaBoxOpen size={32}/>
                    </div>
                    <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">Inventory is empty</p>
                 </div>
              ) : (
                <div className="grid grid-cols-1 gap-10">
                  {Object.entries(getGroupedProducts()).map(([category, products]) => (
                    <div key={category} className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="h-[2px] flex-1 bg-gray-100"></div>
                        <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.3em]">{category} ({products.length})</h3>
                        <div className="h-[2px] flex-1 bg-gray-100"></div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         {products.map((prod) => (
                           <div key={prod.id} className="bg-white p-5 rounded-[25px] flex items-center gap-5 border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                              <div className="w-20 h-20 bg-gray-50 rounded-2xl flex-shrink-0 overflow-hidden border border-gray-50">
                                 {prod.images?.[0] ? (
                                   <img src={prod.images[0].imageUrl} alt="p" className="w-full h-full object-cover"/>
                                 ) : (
                                   <div className="w-full h-full flex items-center justify-center text-gray-200"><FaBoxOpen/></div>
                                 )}
                              </div>
                              <div className="flex-1 min-w-0">
                                 <h4 className="font-black text-gray-900 truncate uppercase text-xs tracking-tight">{prod.productName}</h4>
                                 <p className="text-[10px] text-gray-400 font-bold mt-1">₹ {prod.price.toLocaleString()}</p>
                              </div>
                              <button 
                                onClick={() => deleteProduct(prod.id)}
                                className="w-10 h-10 bg-red-50 text-red-400 rounded-xl hover:bg-red-500 hover:text-white transition-all flex items-center justify-center"
                              >
                                <FaTrashAlt size={14} />
                              </button>
                           </div>
                         ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;